import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, User, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import medicalDB from "../services/database";

const TodaysSchedule = () => {
  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [tomorrowsAppointments, setTomorrowsAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTomorrow, setShowTomorrow] = useState(false);

  useEffect(() => {
    loadSchedule();

    // Load tomorrow's expansion state from localStorage
    const savedExpansion = localStorage.getItem("tomorrowScheduleExpanded");
    if (savedExpansion !== null) {
      setShowTomorrow(savedExpansion === "true");
    }

    // Listen for appointment changes
    const handleAppointmentChange = () => {
      loadSchedule();
    };

    window.addEventListener("appointmentChanged", handleAppointmentChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("appointmentChanged", handleAppointmentChange);
    };
  }, []);

  const loadSchedule = async () => {
    try {
      setLoading(true);
      const [today, tomorrow] = await Promise.all([
        medicalDB.getTodaysAppointments(),
        medicalDB.getTomorrowsAppointments(),
      ]);
      setTodaysAppointments(today);
      setTomorrowsAppointments(tomorrow);
    } catch (error) {
      console.error("Error loading schedule:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTomorrowSchedule = () => {
    const newState = !showTomorrow;
    setShowTomorrow(newState);
    localStorage.setItem("tomorrowScheduleExpanded", newState.toString());
  };

  const handleCheckIn = async (appointment) => {
    if (
      window.confirm(
        `Check in ${appointment.patient?.name} for their ${appointment.startTime} appointment?`
      )
    ) {
      try {
        // Mark appointment as completed
        await medicalDB.updateAppointment(appointment.id, {
          status: "completed",
        });

        // Reload schedule
        loadSchedule();

        // Navigate to visit form with pre-filled data
        window.location.href = `/patients/${appointment.patientId}?newVisit=true&appointmentId=${appointment.id}`;
      } catch (error) {
        console.error("Error checking in:", error);
        alert("Failed to check in. Please try again.");
      }
    }
  };

  const formatTime = (time) => {
    // Convert 24-hour format to 12-hour format
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "cancelled":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  if (loading) {
    return (
      <div className="mt-8 p-4 bg-medical-lightblue dark:bg-medical-dark-lightblue rounded-lg">
        <h3 className="font-medium text-medical-blue dark:text-medical-dark-blue mb-2">
          Today's Schedule
        </h3>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Loading schedule...
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-4" data-testid="todays-schedule">
      {/* Today's Schedule */}
      <div className="p-4 bg-medical-lightblue dark:bg-medical-dark-lightblue rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-medical-blue dark:text-medical-dark-blue">
            Today's Schedule
          </h3>
          <Link
            to="/appointments"
            className="text-xs text-medical-blue dark:text-medical-dark-blue hover:underline"
          >
            View All
          </Link>
        </div>

        {todaysAppointments.length === 0 ? (
          <div className="text-sm text-gray-600 dark:text-gray-400 text-center py-2">
            <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>No appointments today</p>
          </div>
        ) : (
          <div className="space-y-2">
            {todaysAppointments.slice(0, 3).map((appointment) => (
              <div
                key={appointment.id}
                className="p-2 bg-white dark:bg-gray-800 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <Link
                    to={`/appointments`}
                    className="flex items-center space-x-2 min-w-0 flex-1"
                  >
                    <div className="w-6 h-6 bg-medical-blue rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-3 w-3 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {appointment.patient?.name || "Unknown"}
                      </p>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {formatTime(appointment.startTime)}
                        </span>
                      </div>
                    </div>
                  </Link>

                  <div className="flex items-center space-x-1">
                    {appointment.status === "scheduled" && (
                      <button
                        onClick={() => handleCheckIn(appointment)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900 rounded transition-all"
                        title="Check In"
                      >
                        <User className="h-3 w-3" />
                      </button>
                    )}
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        appointment.status
                      )}`}
                    >
                      {appointment.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {todaysAppointments.length > 3 && (
              <Link
                to="/appointments"
                className="block text-center text-xs text-medical-blue dark:text-medical-dark-blue hover:underline py-1"
              >
                +{todaysAppointments.length - 3} more appointments
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Tomorrow's Schedule */}
      <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <button
          onClick={toggleTomorrowSchedule}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="font-medium text-gray-700 dark:text-gray-300">
            Tomorrow ({tomorrowsAppointments.length} appointments)
          </h3>
          {showTomorrow ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>

        {showTomorrow && (
          <div className="mt-3 space-y-2">
            {tomorrowsAppointments.length === 0 ? (
              <div className="text-sm text-gray-600 dark:text-gray-400 text-center py-2">
                <Calendar className="h-6 w-6 mx-auto mb-1 text-gray-400" />
                <p>No appointments tomorrow</p>
              </div>
            ) : (
              <>
                {tomorrowsAppointments.slice(0, 3).map((appointment) => (
                  <Link
                    key={appointment.id}
                    to={`/appointments`}
                    className="block p-2 bg-white dark:bg-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        <div className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-3 w-3 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {appointment.patient?.name || "Unknown"}
                          </p>
                          <div className="flex items-center space-x-1">
                            <Clock className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {formatTime(appointment.startTime)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          appointment.status
                        )}`}
                      >
                        {appointment.status}
                      </span>
                    </div>
                  </Link>
                ))}

                {tomorrowsAppointments.length > 3 && (
                  <Link
                    to="/appointments"
                    className="block text-center text-xs text-gray-600 dark:text-gray-400 hover:underline py-1"
                  >
                    +{tomorrowsAppointments.length - 3} more appointments
                  </Link>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TodaysSchedule;
